import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GET_SUB_ACCOUNT_ENTITIES } from './enterprise-sub-account-entities.gql';
import { EntityPicker, EntitySelection, countSelected } from './EntityPicker';
import { GetSubAccountEntities, GetSubAccountEntitiesVariables } from './__generated__/GetSubAccountEntities';

interface Props {
  open: boolean;
  subTenantId: string;
  subAccountName: string;
  onClose: () => void;
  onAdded: () => void;
}

export const AddEntitiesDialog = (props: Props) => {
  const { open, subTenantId, subAccountName, onClose, onAdded } = props;
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [selected, setSelected] = React.useState<EntitySelection>({});
  const [saving, setSaving] = React.useState(false);

  const { data, loading } = useCampfireQuery<GetSubAccountEntities, GetSubAccountEntitiesVariables>(
    GET_SUB_ACCOUNT_ENTITIES,
    { options: { variables: { subTenantId } } }
  );

  const available = (data?.vm.enterpriseSubAccountEntities ?? []).filter((entity) => !entity.isMapped);
  const selectedCount = countSelected(selected);

  const handleClose = () => {
    setSelected({});
    setSaving(false);
    onClose();
  };

  const handleAdd = () => {
    setSaving(true);

    campfireMutation
      .run({
        url: `${apiUrl}/vm/enterprise/sub-accounts/stamp`,
        method: 'post',
        data: { subTenantId, selection: selected },
      })
      .then((res: any) => {
        if (!res.ok) {
          setSaving(false);
          setSnackbar({
            variant: 'error',
            open: true,
            message: res?.data?.message || 'Could not copy the selection',
          });
          return;
        }

        setSnackbar({
          variant: 'success',
          open: true,
          message: `Copied to ${subAccountName}`,
        });
        setSelected({});
        setSaving(false);
        onAdded();
        onClose();
      })
      .catch(() => {
        setSaving(false);
        setSnackbar({
          variant: 'error',
          open: true,
          message: 'Could not copy the selection',
        });
      });
  };

  if (!open) return null;

  return (
    <FullscreenDialog
      open
      close={handleClose}
      title='Add Entities'
      maxWidth='sm'
      fullWidth
      dialogActions={() => (
        <>
          <TabletButton onClick={handleClose} disabled={saving} style={{ marginRight: '8px' }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleAdd}
            disabled={saving || selectedCount === 0}
          >
            {saving ? 'Copying…' : `Copy ${selectedCount > 0 ? selectedCount : ''}`.trim()}
          </TabletButton>
        </>
      )}
    >
      <Box mt='20px'>
        <Typography variant='body2' style={{ fontSize: '14px', color: '#808080' }}>
          {`Choose what to copy into ${subAccountName}. Anything a selection depends on, such as a task's category, is copied with it.`}
        </Typography>

        <EntityPicker
          entities={available}
          loading={loading || data === undefined}
          selected={selected}
          onChange={setSelected}
          emptyMessage='Everything in this account has already been copied across.'
        />
      </Box>
    </FullscreenDialog>
  );
};
